/** PAGINA «OFERTE» a Planificării: oferta unui furnizor trasă aici devine un
 *  scenariu nou, cu prețurile ei. Documentul e al dosarului: cel urcat aici
 *  apare și în documentele proiectului, iar cel pus acolo la «Rețete &
 *  prețuri» îl citește serverul singur și apare aici ca oricare altă ofertă.
 *
 *  Ce face pagina: urcă fișierul (`oferte.ts::urcaOferta`), întreabă serverul
 *  la câteva secunde cât timp o ofertă e în lucru, iar când oferta s-a aplicat
 *  reîncarcă scenariile (serverul a scris unul nou și l-a pus curent) și lista
 *  de cantități. Fiecare ofertă arată ce a schimbat — țintă cu țintă, prețul
 *  de acum și cel din ofertă, în EUR pe unitatea proiectului — și ce n-a pus,
 *  cu motivul, ca omul să vadă dacă citirea a ales linia bună. */
import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import { numar, t, useT } from "../../i18n";
import { bani, pretUnitar } from "../../structure/bani";
import {
  EXTENSII, aplicaOferta, caleFisier, getOferte, impactDirect, inLucru, stergeOferta, urcaOferta,
  type Oferta, type PretAplicat,
} from "./oferte";
import type { Scenarii } from "./scenarii";

const SONDAJ_MS = 4000;
const UM: Record<string, string> = { m2: "m²", m3: "m³", mp: "m²", mc: "m³" };
const um = (u?: string | null) => (u ? UM[u] ?? u : "");
/** Moneda cum o citește omul: «lei» pentru RON (codul ISO rămâne în date). */
const moneda = (m?: string | null) => (m === "RON" || m === "LEI" ? t("struct.bani.lei") : m ?? "");
const pc = (v: number) => `${v > 0 ? "+" : ""}${numar(v * 100, { maximumFractionDigits: 1 })} %`;

function Stare({ o }: { o: Oferta }) {
  if (o.stare === "se_citeste" || o.stare === "citita") {
    return <span className="ip-pill ip-pill-amb">{t("of.stare.se_citeste")}</span>;
  }
  if (o.stare === "eroare" || (o.stare === "aplicata" && o.eroare)) {
    return <span className="ip-pill ip-pill-bad">{t("of.stare.eroare")}</span>;
  }
  return <span className="ip-pill ip-pill-ok">{t("of.stare.aplicata")}</span>;
}

function Preturi({ aplicate }: { aplicate: PretAplicat[] }) {
  useT();
  return (
    <div className="bc-scroll">
      <table className="sg-table of-tab">
        <thead>
          <tr>
            <th>{t("of.th.tinta")}</th><th>{t("of.th.linie")}</th><th className="bc-n">{t("of.th.oferta")}</th>
            <th className="bc-n">{t("of.th.acum")}</th><th className="bc-n">{t("of.th.nou")}</th>
            <th className="bc-n">Δ</th>
          </tr>
        </thead>
        <tbody>
          {aplicate.map((a) => {
            const d = a.pret_vechi > 0 ? a.pret_nou / a.pret_vechi - 1 : null;
            return (
              <tr key={a.tinta} title={a.motiv}>
                <td>
                  {a.nume}
                  <span className="lc-fel lc-fel-global">{t(`of.fel_tinta.${a.fel_tinta}`)}</span>
                  {a.fel === "lucrare" && <span className="lc-fel lc-fel-sc">{t("of.fel.lucrare")}</span>}
                </td>
                <td className="of-linie">
                  {a.linie}
                  {a.motiv && <div className="sg-muted">{a.motiv}</div>}
                </td>
                <td className="sg-mono bc-n">
                  {numar(a.pret_oferta, { maximumFractionDigits: 2 })} {moneda(a.moneda)}/{um(a.um_oferta) || "?"}
                  {a.factor_um !== 1 && <div className="sg-muted">× {numar(a.factor_um, { maximumFractionDigits: 4 })}</div>}
                  {a.curs != null && (
                    <div className="sg-muted" title={a.curs_estimat ? t("of.curs_estimat_t") : undefined}>
                      {t(a.curs_estimat ? "of.curs_estimat" : "of.curs", { c: numar(a.curs, { maximumFractionDigits: 4 }), m: moneda(a.moneda) })}
                    </div>
                  )}
                </td>
                <td className="sg-mono bc-n">{pretUnitar(a.pret_vechi, um(a.um))}</td>
                <td className="sg-mono bc-n"><strong>{pretUnitar(a.pret_nou, um(a.um))}</strong></td>
                <td className={`sg-mono bc-n ${d == null ? "" : d > 0 ? "of-urc" : "of-cob"}`}>{d == null ? "—" : pc(d)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CardOferta({ o, projectId, sc, potScrie, deschisa, comuta, laScenariu, laAplicaIar, laSterge }: {
  o: Oferta; projectId: string; sc: Scenarii; potScrie: boolean; deschisa: boolean; comuta: () => void;
  laScenariu: (id: string) => void; laAplicaIar: () => void; laSterge: () => void;
}) {
  useT();
  const scen = o.scenariu_id ? sc.stare?.scenarii.find((s) => s.id === o.scenariu_id) ?? null : null;
  const ap = o.aplicare;
  const impact = ap ? impactDirect(ap.aplicate) : null;
  return (
    <div className="of-oferta">
      <div className="of-cap">
        {o.file_id
          ? <a className="of-nume" href={caleFisier(projectId, o.file_id)} target="_blank" rel="noreferrer">{o.nume}</a>
          : <span className="of-nume">{o.nume}</span>}
        <Stare o={o} />
        {o.furnizor && <span className="sg-muted">{o.furnizor}</span>}
        {o.data && <span className="sg-muted">· {o.data}</span>}
        {o.moneda && <span className="sg-muted">· {moneda(o.moneda)}</span>}
        <span className="of-sp" />
        {scen && (
          scen.id === sc.curent.id
            ? <span className="ip-pill ip-pill-acc">{t("of.scenariu_curent", { nume: scen.nume })}</span>
            : <button type="button" className="btn" onClick={() => laScenariu(scen.id)}>
                {t("of.deschide_scenariul", { nume: scen.nume })}</button>
        )}
        {potScrie && (o.stare === "aplicata" || o.stare === "citita") && ap && ap.aplicate.length > 0 && (
          <button type="button" className="btn btng" onClick={laAplicaIar} title={t("of.aplica_iar_t")}>
            {t("of.aplica_iar")}</button>
        )}
        {potScrie && !inLucru(o) && (
          <button type="button" className="btn btng" onClick={laSterge}>{t("of.sterge")}</button>
        )}
      </div>
      {inLucru(o) && <div className="sg-muted" style={{ marginTop: 6 }}>{t("of.se_citeste_d")}</div>}
      {o.eroare && <div className="sg-error" style={{ marginTop: 6 }}>{o.eroare}</div>}
      {o.observatii && <div className="sg-muted" style={{ marginTop: 6 }}>{o.observatii}</div>}
      {ap && (
        <>
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn btng" onClick={comuta} aria-expanded={deschisa}>
              <span className="bc-sag">{deschisa ? "▾" : "▸"}</span>
              {t("of.preturi_puse", { n: ap.aplicate.length })}
              {ap.respinse.length > 0 && <> · {t("of.respinse", { n: ap.respinse.length })}</>}
            </button>
            {impact && impact.cu > 0 && (
              <span className={`sg-mono ${impact.suma > 0 ? "of-urc" : "of-cob"}`} style={{ marginLeft: 10 }}>
                {t("of.impact", { v: `${impact.suma > 0 ? "+" : ""}${bani(impact.suma, { zecimale: 0 })}` })}
              </span>
            )}
          </div>
          {deschisa && (
            <div style={{ marginTop: 8 }}>
              {ap.aplicate.length > 0 && <Preturi aplicate={ap.aplicate} />}
              {ap.respinse.length > 0 && (
                <>
                  <div className="bc-lbl" style={{ margin: "10px 0 4px" }}>{t("of.respinse_titlu")}</div>
                  <ul className="sg-muted" style={{ margin: 0, paddingLeft: 18 }}>
                    {ap.respinse.map((r, i) => (
                      <li key={i}><b>{r.nume ?? r.tinta}</b> — {r.linie ? `«${r.linie}»: ` : ""}{r.motiv}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function PanouOferte({ projectId, solutionId, potScrie, sc, laScenariu, dupaAplicare }: {
  projectId: string;
  /** soluția listei de cantități: candidații ofertei ies din lista ei */
  solutionId: string | null;
  potScrie: boolean;
  sc: Scenarii;
  /** du-te la scenariul ofertei (îl pune curent și arată rezultatele) */
  laScenariu: (id: string) => void;
  /** după ce serverul a făcut un scenariu: lista și rezumatul se refac */
  dupaAplicare: () => void;
}) {
  useT();
  const [oferte, setOferte] = useState<Oferta[] | null>(null);
  const [urcari, setUrcari] = useState<string[]>([]);
  const [eroare, setEroare] = useState<string | null>(null);
  const [peste, setPeste] = useState(false);
  const [deschise, setDeschise] = useState<Set<string>>(new Set());
  const input = useRef<HTMLInputElement | null>(null);
  // scenariile deja știute: unul nou înseamnă că serverul a aplicat o ofertă
  const aplicate = useRef<Set<string> | null>(null);
  const dupa = useRef(dupaAplicare);
  dupa.current = dupaAplicare;
  const scRef = useRef(sc);
  scRef.current = sc;

  const citeste = useCallback(async () => {
    try {
      const r = await getOferte(projectId);
      const noi = r.oferte.filter((o) => o.scenariu_id && !aplicate.current?.has(o.id + o.scenariu_id));
      if (aplicate.current && noi.length) {
        await scRef.current.reincarca().catch(() => {});
        dupa.current();
        // oferta abia aplicată se deschide, ca omul să vadă ce a schimbat
        setDeschise((d) => new Set([...d, ...noi.map((o) => o.id)]));
      }
      aplicate.current = new Set(r.oferte.filter((o) => o.scenariu_id).map((o) => o.id + o.scenariu_id));
      setOferte(r.oferte);
    } catch (e) {
      setEroare((e as Error).message);
    }
  }, [projectId]);
  useEffect(() => { void citeste(); }, [citeste]);

  // sondajul: cât timp o ofertă e în lucru
  const inCurs = urcari.length > 0 || (oferte ?? []).some(inLucru);
  useEffect(() => {
    if (!inCurs) return;
    const id = window.setTimeout(() => void citeste(), SONDAJ_MS);
    return () => window.clearTimeout(id);
  }, [inCurs, oferte, citeste]);

  async function urca(fisiere: File[]) {
    if (!solutionId) return;
    setEroare(null);
    for (const f of fisiere) {
      const ext = f.name.slice(f.name.lastIndexOf(".")).toLowerCase();
      if (!EXTENSII.includes(ext)) {
        setEroare(t("of.format", { nume: f.name }));
        continue;
      }
      setUrcari((u) => [...u, f.name]);
      try {
        const o = await urcaOferta(projectId, solutionId, f);
        setOferte((l) => [o, ...(l ?? []).filter((x) => x.id !== o.id)]);
      } catch (e) {
        setEroare(t("of.nu_s_a_urcat", { nume: f.name, e: (e as Error).message }));
      } finally {
        setUrcari((u) => u.filter((x) => x !== f.name));
      }
    }
  }

  const peZona = (e: DragEvent) => { e.preventDefault(); setPeste(true); };
  const lasa = (e: DragEvent) => {
    e.preventDefault();
    setPeste(false);
    void urca([...e.dataTransfer.files]);
  };

  return (
    <div className="bc">
      <p className="bc-indiciu">{t("of.intro")}</p>
      {potScrie && solutionId && (
        <div className={`of-zona${peste ? " of-zona-peste" : ""}`} role="button" tabIndex={0}
             aria-label={t("of.zona_titlu")}
             onClick={() => input.current?.click()}
             onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); input.current?.click(); } }}
             onDragOver={peZona} onDragEnter={peZona} onDragLeave={() => setPeste(false)} onDrop={lasa}>
          <div className="of-zona-titlu">{t("of.zona_titlu")}</div>
          <div className="sg-muted">{t("of.zona_d")}</div>
          {urcari.length > 0 && <div style={{ marginTop: 8 }}>{t("of.se_urca", { n: urcari.join(", ") })}</div>}
          <input ref={input} type="file" multiple hidden accept={EXTENSII.join(",")}
                 onChange={(e) => { void urca([...(e.target.files ?? [])]); e.target.value = ""; }} />
        </div>
      )}
      {!solutionId && <div className="sg-muted">{t("of.fara_lista")}</div>}
      {eroare && <div className="sg-error" style={{ marginTop: 8 }}>{eroare}</div>}
      {oferte && oferte.length === 0 && <div className="sg-muted" style={{ marginTop: 12 }}>{t("of.niciuna")}</div>}
      {(oferte ?? []).map((o) => (
        <CardOferta key={o.id} o={o} projectId={projectId} sc={sc} potScrie={potScrie}
                    deschisa={deschise.has(o.id)}
                    comuta={() => setDeschise((d) => { const n = new Set(d); if (n.has(o.id)) n.delete(o.id); else n.add(o.id); return n; })}
                    laScenariu={laScenariu}
                    laAplicaIar={() => {
                      void aplicaOferta(projectId, o.id).then(() => citeste()).catch((e) => setEroare((e as Error).message));
                    }}
                    laSterge={() => {
                      if (!window.confirm(t("of.sterge_confirma", { nume: o.nume }))) return;
                      void stergeOferta(projectId, o.id).then(() => citeste()).catch((e) => setEroare((e as Error).message));
                    }} />
      ))}
    </div>
  );
}
